export class Vec2 {
    x: number;
    y: number;

    constructor(x: number = 0, y: number = 0) {
        this.x = x;
        this.y = y;
    }
}

export class Vertex {
    next?: Vertex;

    constructor(pos?: Vec2, inorm?: Vec2, onorm?: Vec2) {
        this.pos = pos ?? new Vec2();
        this.inorm = inorm ?? new Vec2();
        // If no differing outgoing normal is specified, then assume it is
        // identical to the incoming normal
        this.onorm = onorm ?? this.inorm;
    }

    /**
     * The position of the vertex.
     */
    pos: Vec2;

    /**
     * The normal vector at this vertex for the incoming edge, i.e. the edge
     * that is the line segment between `this.prev.pos` and `this.pos`.
     */
    inorm: Vec2;

    /**
     * The normal vector at this vertex for the outgoing edge, i.e. the edge
     * that is the line segment between `this.pos` and `this.next.pos`.
     */
    onorm: Vec2;

    /**
     * The length of the outgoing edge.
     */
    olength: number = 0;

    /**
     * The global measure of the outgoing angle of the edge following this
     * vertex.
     */
    oangle: number = 0;

    /**
     * Half the measure of the angle between the incoming and outgoing edges.
     */
    halfAngle: number = 0;

    /**
     * Iterate over the remaining items in the vertex list after this one.
     */
    *rest() {
        let v = this.next;
        while(v != null) {
            yield v;
            v = v.next;
        }
    }
}

export class VertexList {
    first: Vertex;
    last: Vertex;

    length: number = 0;

    closed: boolean = false;

    /**
     * The vertex list starts at length zero. Call `moveTo` to set the position
     * of the list's initial vertex and increment the length to one. After
     * that, calls to addVertex will push a new vertex onto the list, in effect
     * adding an edge.
     */
    constructor() {
        this.last = this.first = new Vertex();
    }

    moveTo(x: number, y: number) {
        this.last.pos.x = x;
        this.last.pos.y = y;
        this.length = 1;
    }
    
    addVertex(pos?: Vec2, inorm?: Vec2, onorm?: Vec2) {
        const
            v = new Vertex(pos, inorm, onorm),
            dx = pos.x - this.last.pos.x,
            dy = pos.y - this.last.pos.y;

        this.last.next = v;
        this.last = v;
        this.length++;
    }

    /**
     * Create an edge to the given coordinate with normals that are
     * perpendicular to the edge, i.e. flat shaded.
     *
     * Note that edgeTo is useful when you know the coordinate of the
     * destination and want the normal calculated for you. If you only know
     * the offset and not the coordinate, edgeToRelative may be the method you
     * want.
     */
    edgeTo(x: number, y: number) {
        const
            // [dx, dy] expresses the edge as a vector from last.pos to [x, y]
            dx = x - this.last.pos.x,
            dy = y - this.last.pos.y,
            r = Math.sqrt(dx*dx + dy*dy),

            // The normal perpendicular to the edge is the negative reciprocal
            // of the edge-vector, divided through by length, r, to normalize
            // the normal to unit length
            norm = new Vec2(dy / r, -dx / r);
        this.last.onorm = norm;
        if(this.last == this.first) { this.last.inorm = norm; }
        this.addVertex(new Vec2(x, y), norm);
    }

    /**
     * Like edgeTo but uses an offset relative to the last vertex rather than
     * absolute coordinates.
     */
    edgeToRelative(dx: number, dy: number) {
        // nx, ny are a 90 degree clockwise rotation from the vector (dx, dy),
        // and they're normalized so that they'll give a good dot product later.
        const
            r = Math.sqrt(dx*dx + dy*dy),
            pos = new Vec2(this.last.pos.x + dx, this.last.pos.y + dy),
            norm = new Vec2(dy / r, -dx / r);
        this.last.onorm = norm;
        if(this.last == this.first) { this.last.inorm = norm; }
        this.addVertex(pos, norm);
    }

    /**
     * If you just need a rectangle, this is a simple one-liner to call. If you
     * use this, you probably shouldn't use anything else.
     */
    rect(x: number, y: number, width: number, height: number) {
        const
            ax = x,       ay = y,
            bx = x+width, by = y,
            cx = bx,      cy = y+height,
            dx = x,       dy = cy;
        this.moveTo(ax, ay);
        this.edgeTo(bx, by);
        this.edgeTo(cx, cy);
        this.edgeTo(dx, dy);
        this.close();

        // I don't love setting these explicitly, but I'm not sure there's a
        // good way to read the caller's mind when they use the `edgeTo()`
        // method.
        this.first.inorm = new Vec2(-1, 0);
        this.last.onorm = new Vec2(-1, 0);
    }

    /**
     * Starting from wherever the last point was drawn, draw an arc with n
     * segments. The minimum n is 1, lower values are ignored.
     * @param start - The angle in radians of the start of the arc segment
     * @param theta - the magnitude of the span of the arc to be drawn
     * @param r - the radius of the arc to draw
     * @param n - the number of segments to draw; 1 represents a single edge
     */
    arc(start: number, theta: number, r: number, n: number) {
        n = Math.max(1, n);
        const
            x = this.last.pos.x,
            y = this.last.pos.y,
            cosStart = Math.cos(start),
            sinStart = Math.sin(start),
            cx = x - r * cosStart,
            cy = y - r * sinStart,
            inc = theta / n,
            sign = theta > 0 ? 1 : -1;

        this.last.onorm = new Vec2(sign*cosStart, sign*sinStart);

        for (let i = 1; i <= n; i++) {
            const
                t = start + i * inc,
                cos = Math.cos(t),
                sin = Math.sin(t),
                pos = new Vec2(
                    cx + (r * cos),
                    cy + (r * sin)),
                norm = new Vec2(sign*cos, sign*sin);

            this.addVertex(pos, norm, norm);
        }
    }

    /**
     * Closes the shape by setting the closed property to true.
     *
     * We don't set the `next` property of the last vertex because that would
     * mean we'd have to check `next != null && next != first` to prevent
     * infinite loops, whereas leaving it null we can safely loop with
     * `while(next != null)` and check the `closed` property after the loop to
     * decide whether to add the last edge after the last vertex.
     */
    close() {
        this.closed = true;
    }

    /**
     * Computes the `olength`, `oangle`, and `halfAngle` properties.
     */
    computeAdditionalProperties() {
        let
            v = this.first,
            lastOAngle: number;
        while(v.next != null) {
            const
                dx = v.next.pos.x - v.pos.x,
                dy = v.next.pos.y - v.pos.y;
            v.olength = Math.sqrt(dx*dx + dy*dy);
            v.oangle = lastOAngle = Math.atan2(dy, dx);
            v = v.next;
        }
        if(this.closed) {
            const
                dx = this.first.pos.x - this.last.pos.x,
                dy = this.first.pos.y - this.last.pos.y;
            this.last.oangle = Math.atan2(dy, dx);
        } else {
            this.last.oangle = lastOAngle;
        }

        v = this.first;
        while(v.next != null) {
            v.next.halfAngle = (v.oangle - v.next.oangle)/2;
            v = v.next;
        }

        if(this.closed) {
            this.first.halfAngle = (this.last.oangle - this.first.oangle)/2;
            this.last.halfAngle = (this.last.oangle - this.first.oangle)/2;
        } else {
            this.first.halfAngle = 0;
            this.last.halfAngle = 0;
        }
    }

    /**
     * Iterates through the whole list from the first.
     */
    *[Symbol.iterator]() {
        if(this.length == 0) return;
        if(this.length == 1) return this.last;
        yield this.first;
        yield* this.first.rest();
    }
}
